import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Spinner,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { ActivityTimeline } from '@/components/userActivity/ActivityTimeline'
import { LoginHistory } from '@/components/userActivity/LoginHistory'
import { ActiveSessions } from '@/components/userActivity/ActiveSessions'
import { ActivityStatistics } from '@/components/userActivity/ActivityStatistics'
import { useThemeColors } from '@/hooks/useThemeColors'

export default function UserActivityPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const params = useParams()
  const [isValidating, setIsValidating] = useState(true)
  const [activeTab, setActiveTab] = useState(0)

  // Extract userId from URL path as fallback if route params aren't working
  const extractUserIdFromPath = (): number | null => {
    const pathMatch = window.location.pathname.match(/\/user-management\/user-activity\/(\d+)/)
    if (pathMatch && pathMatch[1]) {
      const extracted = Number(pathMatch[1])
      if (!Number.isNaN(extracted) && extracted > 0) {
        return extracted
      }
    }
    return null
  }

  // Try both userId and id parameter names (in case route uses :id instead of :userId)
  const userIdParam = params.userId || params.id
  const userIdFromParams = userIdParam ? Number(userIdParam) : undefined
  const userIdFromPath = extractUserIdFromPath()
  const userId = userIdFromParams || userIdFromPath || undefined

  useEffect(() => {
    // Debug logging
    if (import.meta.env.DEV) {
      console.log('[UserActivity] Route params:', { 
        allParams: params,
        userId: params.userId,
        id: params.id,
        userIdParam,
        userIdFromParams,
        userIdFromPath,
        finalUserId: userId,
        location: window.location.pathname 
      })
    }

    // Validate userId
    if (!userId || Number.isNaN(userId) || userId <= 0) {
      console.warn('[UserActivity] Invalid userId, redirecting to users list', {
        userIdParam,
        userIdFromParams,
        userIdFromPath,
        finalUserId: userId
      })
      navigate('/user-management/users', { replace: true })
      return
    }
    
    // Valid userId found, stop validating
    setIsValidating(false)
  }, [userId, navigate])

  if (isValidating || !userId) {
    return (
      <AdminLayout>
        <Box p={4}>
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0} pb={2}>
            <Heading size="md">User Activity</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Monitor user login history, session activity, profile changes, and user-specific actions
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0} pt={0}>
            <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
              <TabList>
                <Tab>Timeline</Tab>
                <Tab>Login History</Tab>
                <Tab>Active Sessions</Tab>
                <Tab>Statistics</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0} pt={4}>
                  <ActivityTimeline userId={userId} />
                </TabPanel>
                <TabPanel px={0} pt={4}>
                  <LoginHistory userId={userId} />
                </TabPanel>
                <TabPanel px={0} pt={4}>
                  <ActiveSessions userId={userId} />
                </TabPanel>
                <TabPanel px={0} pt={4}>
                  <ActivityStatistics userId={userId} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </Box>
    </AdminLayout>
  )
}


